import {
  ChangeEventHandler,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Icon from '@ant-design/icons';
import shallow from 'zustand/shallow';
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import { Form, FormProps } from 'antd';
import { useSelector } from 'react-redux';

// components
import BuyLeverage from 'features/day-trade/components/BuyLeverage/leverage';
import ChevronDownSvg from 'global/Securities/components/Icons/ChevronDownSvg';
import Chips from 'global/Securities/components/Chips';
import If from 'global/ErrorHandlers/If';
import InputStepper from 'global/Securities/components/InputStepper';
import OrderTypeFormItem from 'global/Securities/components/Form/TransactionForm/OrderTypeFormItem';
import OverLimitText from 'global/TradingModal/components/OverLimitText';
import Slider from 'global/Securities/components/Slider';
import SpecialMonitoringBanner from 'global/TradingModal/components/SpecialMonitoringBanner';
import SplitOrderFormItem from 'global/Securities/components/Form/TransactionForm/SplitOrderFormItem';
import Tooltip from 'global/Securities/components/Tooltip';
import TriggerPriceTooltip from 'global/TradingModal/components/TriggerPriceTooltip';
import { DayTradeLeverage } from 'features/day-trade/components/Modals';
import { LightningSvg } from 'features/day-trade/components/Icons';

// hooks
import useCashInfoStore from 'global/Securities/stores/useCashInfoStore';
import useEnableSplitOrder from 'global/TradingModal/hooks/useEnableSplitOrder';
import useTransactionBalance from 'global/TradingModal/hooks/useTransactionBalance';
import useIsUserSyariah from 'global/TradingModal/hooks/useIsUserSyariah';
import useIsSecuritiesMainAccount from 'hooks/useIsSecuritiesMainAccount';

// lib
import tradingDataStore from 'lib/stores/tradingData';
import { VIRTUAL_TRADING_TYPES } from 'lib/analytics/virtualTrading';
import { selectors as profileSelectors } from 'lib/entities/profile';
import {
  clevertapBuyError,
  clevertapOrderPlaced,
  clevertapOrderQuantityAdjusted,
  clevertapDayTradeOrderTypeSelected,
  clevertapDayTradeSplitOrderSelected,
  clevertapBuyOrderViewed,
} from 'lib/analytics/securities';
import {
  trackAutoBuyOrderPlaced,
  trackAutoBuyOrderTypeInfoSelected,
  trackAutoBuyOrderTypeSelected,
  trackAutoBuyOrderView,
  trackAutoBuyTriggerConditionSelected,
} from 'lib/analytics/autoOrder';

// stores
import realTradingStore from 'global/TradingModal/RealTradingModal/store';
import tradingModalStore from 'global/TradingModal/store';
import useUIConfigStore from 'stores/useUIConfigStore';
import { useTradingInfoStore } from 'global/Securities/stores/useTradingInfoStore';

// utils
import { formatNumber } from 'utils/number';
import { getCashTracks } from 'global/Securities/utils/transactionBalance';
import { getLimitLot } from 'global/Securities/utils/formula';
import { getLotOrderLimit } from 'global/TradingModal/RealTradingModal/utils';
import { getStepFractionByValue } from 'global/Securities/utils/transactionPrice';
import {
  generateTradingVariable,
  checkIsAutoOrderTncAgreed,
  calculateLotAmendTransaction,
} from 'global/TradingModal/RealTradingModal/TransactionModal/utils';
import { generateOrderTypeLabel } from 'utils/securities/analytics';

// constants
import { BUY_ORDER_TYPE_TEXT, ORDER_TYPE, STOCK_TYPE } from 'constants/trading';
import {
  balanceLabel,
  expiryOptions,
} from 'global/Securities/constants/transactionForm';
import {
  DATA_CY_MODAL_TRADING,
  lotSize,
  triggerPriceOption,
} from 'global/TradingModal/constant';

// types
import { AutoOrderProduct } from 'global/Securities/types/autoOrder';
import {
  BuyOrderType,
  Expiry,
  PaymentMethod,
  TransactionType,
  SplitOrderMethodEnum,
} from 'global/Securities/types/transaction';
import {
  TradingFeatureEnum,
  EventTypeEnum,
} from 'global/Securities/types/trading';

// styles
import { ButtonWrapper, OrderButton } from 'global/TradingModal/styled';
import {
  CustomSelect,
  TriggerPriceSelect,
} from 'global/TradingModal/RealTradingModal/styled';
import useTradingStore from 'features/transaction/useTradingStore';
import {
  BalanceButton,
  FieldsWrapper,
  SliderPercentText,
  TransactionForm,
} from './styled';

interface FormBuyProps {
  showOrderTypeModal: () => void;
  showAutoBuyTncModal: () => void;
  showSpecialMonitoringModal: () => void;
  onShowTradingLimitTnc?(): void;
  onShowSplitOrderInfo?(): void;
  onShowSplitOrderMethodInfo?(): void;
  onSubmit: () => void;
  onShowDayTradeOnboarding?(): void;
  onShowDayTradeUnavailable?(): void;
}

const FormBuy = ({
  showOrderTypeModal,
  showAutoBuyTncModal,
  showSpecialMonitoringModal,
  onShowTradingLimitTnc = () => {},
  onShowSplitOrderInfo = () => {},
  onShowSplitOrderMethodInfo = () => {},
  onSubmit,
  onShowDayTradeOnboarding = () => {},
  onShowDayTradeUnavailable = () => {},
}: FormBuyProps) => {
  const [form] = Form.useForm();

  const [
    setUIValue,
    isTradingLimitTooltipOpened,
    defaultPaymentMethod,
    isDayTradeSubAccountActive,
  ] = useUIConfigStore(
    (s) => [
      s.setUIValue,
      s.getUIValue('tradingLimitTooltip'),
      s.getUIValue('transactionPaymentMethod'),
      s.getUIValue('dayTradeSubAccountActive'),
    ],
    shallow,
  );

  const [cashInfoLoaded, setCashInfoLoaded] = useState<boolean>(false);
  const [hasFormError, setHasFormError] = useState<boolean>(false);
  const [isPaymentMethodVisible, setPaymentMethodVisible] =
    useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    defaultPaymentMethod || PaymentMethod.TradingBalance,
  );

  const [
    activeTradingBalance,
    tradingBalance,
    tradingLimit,
    dayTradeBuyingPower,
  ] = useTransactionBalance(paymentMethod);

  const loadTradingInfo = useTradingInfoStore((s) => s.loadTradingInfo);
  const splitOrder = useTradingInfoStore((s) => s.splitOrder);
  const { max_order: maxOrder } = splitOrder || {};

  const [cashInfoError, isCashInfoLoading, loadCashInfo] = useCashInfoStore(
    (s) => [s.error, s.isLoading, s.loadCashInfo],
    shallow,
  );

  const {
    isAutoBuy,
    orderedPrice,
    orderedLot,
    modalData,
    isDayTrade,
    orderid: orderId,
    setAutoBuy,
    setModalState,
  } = tradingModalStore();

  const { symbol, orderType } = useTradingStore((s) => ({
    symbol: s.symbol,
    orderType: s.orderType,
  }));

  const { trigger_price, config_type, amount_open_fee, amount_matched_total } =
    modalData || {};

  const {
    portfolio: {
      data: { show_create_smartorder: showCreateSmartOrder },
    },
    company: {
      data: {
        tradeable,
        type_company: productType,
        market_hour: marketHour,
        day_trade_info: dayTradeInfo,
      },
    },
    cashOnHand,
    getCashOnHand,
    configFormula: { data: configFormula },
  } = tradingDataStore();

  const { multiplier } = dayTradeInfo || {};

  const {
    configType,
    price,
    expiry,
    triggerPrice,
    shares,
    type: buyOrderType,
    isSplitOrderActive,
    splitQuantity,
    splitRangeMin,
    splitRangeMax,
    splitMethod,
    setTriggerPrice,
    setPrice,
    setExpiry,
    setConfigType,
    setOrderType,
    setShares,
    setSplitOrderActive,
    setSplitQuantity,
    setSplitRange,
    setSplitMethod,
    setDayTradeActive,
    setDayTadeMultiplier,
  } = realTradingStore();

  const isShowSplitOrder = useEnableSplitOrder('buy');
  const isUserSyariah = useIsUserSyariah();
  const isMainAccount = useIsSecuritiesMainAccount();

  const isSaham = productType.toLowerCase() === STOCK_TYPE.saham;
  const isRight = productType.toLowerCase() === STOCK_TYPE.right;
  const isAmend = orderType === ORDER_TYPE.AMEND_BUY;

  const isShowOrderType =
    buyOrderType && isSaham && showCreateSmartOrder && !isAmend;
  const originalLot = shares / lotSize;
  const isTradeable = tradeable === 1;

  // trigger price must be > 0, if order type is Auto Buy
  const triggerAutoBuy =
    buyOrderType === BuyOrderType.AutoBuy && Number(triggerPrice) === 0;
  const hideExpiry = !isSaham || isAmend;

  const dayTradeMultiplier = parseInt(multiplier, 10);
  const isPaymentDayTradeActive = paymentMethod === PaymentMethod.DayTrade;
  const isPaymentTradingLimitActive =
    paymentMethod === PaymentMethod.TradingLimit;

  // Disable day trade if multiplier is below 1
  const isPaymentDayTradeDisabled = useMemo(
    () => dayTradeMultiplier <= 1,
    [dayTradeMultiplier],
  );

  // Hide or show payment leverage (Trading Limit / Day Trade) - Buy Order
  const isPaymentMethodDisabled = useMemo(() => {
    const isOrderTypeAutoBuy = buyOrderType === BuyOrderType.AutoBuy;
    const isExpiryGtc = expiry === Expiry.GoodTillCancelled;

    return [
      isOrderTypeAutoBuy,
      isExpiryGtc,
      isAmend,
      !isSaham,
      isUserSyariah,
      !isMainAccount,
    ].includes(true);
  }, [
    buyOrderType,
    expiry,
    dayTradeMultiplier,
    isAmend,
    isSaham,
    isUserSyariah,
    isMainAccount,
  ]);

  // Hide or show payment leverage (Trading Limit / Day Trade) - Amend Order
  const isShowAmendLeverage = useMemo(
    () => isDayTrade && isAmend,
    [isDayTrade, isAmend],
  );

  const isTradingLimitTooltipActive = useMemo(
    () =>
      [
        !isPaymentMethodDisabled,
        !isTradingLimitTooltipOpened,
        isShowOrderType,
        false, // Make it false, because tooltip is not shown anymore
      ].every((value) => value),

    [isPaymentMethodDisabled, isTradingLimitTooltipOpened, isShowOrderType],
  );

  // Balance for payment method (leverage) modal
  const leverageBalanceList = useMemo(() => {
    const balance = {
      [PaymentMethod.TradingLimit]: tradingLimit,
      [PaymentMethod.DayTrade]: dayTradeBuyingPower,
    };

    // Remove day trade balance if multiplier is below 1
    if (dayTradeMultiplier <= 1) {
      delete balance[PaymentMethod.DayTrade];
    }

    return balance;
  }, [tradingLimit, dayTradeBuyingPower, dayTradeMultiplier]);

  const tracksWidth = useMemo(
    () => getCashTracks(paymentMethod, tradingBalance, tradingLimit),
    [paymentMethod, tradingBalance, tradingLimit],
  );

  const {
    data: { username },
  } = useSelector(profileSelectors);

  const {
    data: { available_cash_on_hand },
  } = cashOnHand;

  const limitLot = useMemo(() => {
    const buyOrderDayTrade = isPaymentDayTradeActive;

    // New limit lot calculation for amend order
    // If user is day trade and day trade is active, use active trading balance (from buying power)
    // No need to add fee and matched total because it's already included in the buying power
    // Otherwise, use original trading balance plus fee and matched total
    if (isAmend) {
      const amendTradingBalance = isDayTrade
        ? activeTradingBalance
        : activeTradingBalance +
          (amount_open_fee || 0) +
          (amount_matched_total || 0);
      return getLimitLot(amendTradingBalance, price, buyOrderDayTrade) || 1;
    }

    // Set limit to 1 when limit lot equal zero to enable slider to 100%
    return getLimitLot(activeTradingBalance, price, buyOrderDayTrade) || 1;
  }, [activeTradingBalance, price, isPaymentDayTradeActive, isAmend]);

  const {
    TRADING_BALANCE: tradingbalance,
    BUY_AMOUNT_FEE: investmentPlusFee,
    LOT: lot,
  } = generateTradingVariable({
    configFormula,
    cash_on_hand: available_cash_on_hand,
    trading_balance: activeTradingBalance,
    price,
    lot: originalLot,
    trigger_price: triggerPrice || 0,
    config_type: configType || 0,
    order_type: orderType,
    previous_buy_price: orderedPrice,
    previous_lot: orderedLot,
    stock_type: productType,
    trading_balance_amended:
      activeTradingBalance +
      (amount_open_fee || 0) +
      (amount_matched_total || 0),
    cash_onhand_amended:
      available_cash_on_hand +
      (amount_open_fee || 0) +
      (amount_matched_total || 0),
    isDayTrade: isPaymentDayTradeActive,
  });

  const isOverlimit = investmentPlusFee > tradingbalance;

  const isDisabledBuySplitOrder = useMemo(() => {
    if (!isSplitOrderActive) {
      return false;
    }

    if (splitQuantity === null || splitQuantity < 1) {
      return true;
    }

    if (
      splitMethod === SplitOrderMethodEnum.RANDOM &&
      (!splitRangeMin || !splitRangeMax)
    ) {
      return true;
    }

    return false;
  }, [
    isSplitOrderActive,
    splitQuantity,
    splitMethod,
    splitRangeMin,
    splitRangeMax,
  ]);

  const disableBuy = useMemo(
    () =>
      price === 0 ||
      lot < 1 ||
      !isTradeable ||
      isOverlimit ||
      triggerAutoBuy ||
      hasFormError ||
      isDisabledBuySplitOrder,
    [
      hasFormError,
      isTradeable,
      isOverlimit,
      price,
      lot,
      triggerAutoBuy,
      isDisabledBuySplitOrder,
    ],
  );

  const showSplitOrderForm = useMemo(
    () => isShowSplitOrder && paymentMethod === PaymentMethod.TradingBalance,
    [isShowSplitOrder, paymentMethod],
  );

  // Get balance label based on payment method (leverage)
  const getBalanceLabel = useCallback(() => {
    if (isRight) return balanceLabel.cashOnHand;

    const dayTradeEnable = isPaymentDayTradeActive && dayTradeMultiplier > 1;
    if (dayTradeEnable) {
      return (
        <>
          {balanceLabel.dayTrade}{' '}
          <Chips className="multiplier-chip">
            <Icon component={LightningSvg} />
            {`${multiplier}x`}
          </Chips>
        </>
      );
    }

    return isPaymentTradingLimitActive
      ? balanceLabel.tradingLimit
      : balanceLabel.tradingBalance;
  }, [isRight, isPaymentDayTradeActive, dayTradeMultiplier, paymentMethod]);

  const getMaxLot = () => {
    if (orderedPrice && price === orderedPrice) return orderedLot;
    return getLotOrderLimit();
  };

  const getSliderMaxLot = () => {
    const lotOrderLimit = getLotOrderLimit();
    return limitLot > lotOrderLimit ? lotOrderLimit : limitLot;
  };

  const getPercentage = () => {
    const percentage = Math.round((100 * lot) / limitLot);

    if (Number.isNaN(percentage) || percentage === Infinity) {
      return '0';
    }

    return percentage;
  };

  const handleValidateAllFields = () => {
    form.validateFields();
  };

  const handleSetPaymentMethod = (method: PaymentMethod) => {
    setPaymentMethod(method);
    setUIValue('transactionPaymentMethod', method);

    if (method !== PaymentMethod.TradingBalance) {
      setSplitOrderActive(false);
      form.setFieldsValue({ isSplitOrderActive: false });
    }

    if (method !== paymentMethod && !isAmend) {
      setShares(0);
    }
  };

  const handleLoadCashInfo = useCallback(async () => {
    try {
      if (cashInfoLoaded) return;

      const cashOrderId = isAmend && isDayTrade ? orderId : undefined;

      await loadCashInfo(symbol, cashOrderId);
      setCashInfoLoaded(true);
    } catch (error) {
      handleSetPaymentMethod(PaymentMethod.TradingBalance);
    }
  }, [cashInfoLoaded, setCashInfoLoaded]);

  const handleSaveFields = () => {
    const values = form.getFieldsValue();
    if (isShowSplitOrder && !isPaymentDayTradeActive) {
      setSplitOrderActive(!!values.isSplitOrderActive);
      setSplitQuantity(values.splitQuantity);
      setSplitRange(values.splitRangeMin, values.splitRangeMax);
    } else {
      setSplitOrderActive(false);
      setSplitQuantity(null);
      setSplitRange(null, null);
    }

    const DayTradeOrder = isDayTrade || isPaymentDayTradeActive;
    setDayTradeActive(DayTradeOrder);
    setDayTadeMultiplier(DayTradeOrder ? dayTradeMultiplier : 0);
  };

  const handleFieldsChange: FormProps['onFieldsChange'] = (_, allFields) => {
    const hasEmpty = allFields.some(({ value }) => value === undefined);
    const hasError = allFields.some(({ errors }) => errors.length > 0);
    setHasFormError(hasEmpty || hasError);
  };

  const handleOrderTypeChange = (selectedOrderType: BuyOrderType) => {
    const isSelectedAutoBuy = selectedOrderType === BuyOrderType.AutoBuy;
    if (isSelectedAutoBuy) {
      setSplitOrderActive(false);
      form.setFieldsValue({ isSplitOrderActive: false });
    }

    if (
      isSelectedAutoBuy &&
      !checkIsAutoOrderTncAgreed(username, AutoOrderProduct.AutoBuy)
    ) {
      showAutoBuyTncModal();
      return;
    }
    setOrderType(selectedOrderType);
    setAutoBuy(isSelectedAutoBuy);

    if (isSelectedAutoBuy) handleSetPaymentMethod(PaymentMethod.TradingBalance);

    trackAutoBuyOrderTypeSelected(selectedOrderType, symbol);

    clevertapDayTradeOrderTypeSelected(
      symbol,
      generateOrderTypeLabel(selectedOrderType),
    );
  };

  const handleBalanceLabelClick = () => {
    setPaymentMethodVisible(true);
    handleLoadCashInfo();
  };

  const handleTradingLimitTooltipChange = (value: boolean) => {
    if (!value) setUIValue('tradingLimitTooltip', !value);
  };

  const handlePriceChange = (value: number) => {
    clevertapOrderQuantityAdjusted({
      symbol,
      orderType,
      adjustedType: 'add/substract',
    });

    setPrice(value);
    handleValidateAllFields();

    if (isAmend) {
      const PRICE = Number(value);
      const LOT = Math.round(Number(originalLot));

      const lotAmend = calculateLotAmendTransaction(
        orderedPrice,
        orderedLot,
        PRICE,
        LOT,
      );

      setShares(lotAmend * lotSize);
    }
  };

  const handleMethodChange = (method: PaymentMethod) => {
    // Handle when use click both tooltip and balance button
    if (isTradingLimitTooltipActive) setUIValue('tradingLimitTooltip', true);

    if (method === PaymentMethod.TradingLimit) {
      onShowTradingLimitTnc();
    }

    setPaymentMethodVisible(false);
    handleSetPaymentMethod(method);
  };

  const handleMethodClose = () => {
    setPaymentMethodVisible(false);
  };

  const handleOrderTypeInfoClick = () => {
    showOrderTypeModal();
    trackAutoBuyOrderTypeInfoSelected(BUY_ORDER_TYPE_TEXT[orderType], symbol);
  };

  const handleLotChange = (value: number) => {
    clevertapOrderQuantityAdjusted({
      symbol,
      orderType,
      adjustedType: 'add/substract',
    });
    const roundLot = Math.floor(value);
    setShares(roundLot * lotSize);
    handleValidateAllFields();
  };

  const handleExpiryChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const selectedExpiry = e.target.value as Expiry;
    setExpiry(selectedExpiry);

    if (selectedExpiry === Expiry.GoodTillCancelled) {
      handleSetPaymentMethod(PaymentMethod.TradingBalance);
    } else {
      setSplitOrderActive(false);
      form.setFieldsValue({ isSplitOrderActive: false });
    }
    handleValidateAllFields();
  };

  const handleSubmit = async () => {
    handleSaveFields();

    if (isPaymentDayTradeActive) {
      const { day_trade } = await loadTradingInfo([
        TradingFeatureEnum.DAY_TRADE,
      ]);

      // only check if day trade response is available
      if (day_trade) {
        const isTimeLimit = day_trade.remaining_trading_close <= 0;

        const isUnavailable = [
          EventTypeEnum.EXCEED_DEBT_RATIO,
          EventTypeEnum.OUTSIDE_MARKET_HOURS,
        ].includes(day_trade?.event_type);

        // show day trade unavailable if day trade is not allowed
        if (isUnavailable || isTimeLimit) {
          onShowDayTradeUnavailable();
          return;
        }
      }
    }

    onSubmit();
    clevertapOrderPlaced({
      orderType,
      symbol,
      price,
      lot,
      tradingBalancePercentage: getPercentage(),
      status: disableBuy ? 'failed' : 'success',
      expiry: expiryOptions[expiry],
      type: VIRTUAL_TRADING_TYPES.realTrading,
      orderTypeStatus: generateOrderTypeLabel(buyOrderType),
      splitOrder: isSplitOrderActive,
      dayTrade: isPaymentDayTradeActive,
    });

    if (buyOrderType === BuyOrderType.AutoBuy) trackAutoBuyOrderPlaced();
  };

  const handleChangeSwitchSplitOrder = (checked: boolean) => {
    setSplitOrderActive(checked);

    if (!checked) {
      setSplitMethod(SplitOrderMethodEnum.RANDOM);
    }

    if (isPaymentDayTradeActive && checked) {
      setPaymentMethod(PaymentMethod.TradingBalance);
    }

    clevertapDayTradeSplitOrderSelected(checked);
  };

  useEffect(() => {
    if (isRight) getCashOnHand();
    trackAutoBuyOrderView();
  }, [isRight]);

  useEffect(() => {
    if (isAmend) {
      if (orderedPrice) setPrice(orderedPrice);
      if (trigger_price) setTriggerPrice(trigger_price);
      if (orderedLot) setShares(orderedLot * lotSize);
      if (triggerPriceOption) {
        setConfigType(triggerPriceOption[config_type - 1]);
      }
    }

    if (isAutoBuy) {
      setOrderType(BuyOrderType.AutoBuy);
      handleSetPaymentMethod(PaymentMethod.TradingBalance);
    } else {
      setOrderType(BuyOrderType.LimitOrder);
    }

    if (!isSaham) {
      setExpiry(Expiry.GoodForDay);
      setAutoBuy(false);
      setOrderType(BuyOrderType.LimitOrder);
    }
  }, [orderType, isAutoBuy]);

  useEffect(() => {
    if (isOverlimit) clevertapBuyError();
  }, [isOverlimit]);

  useEffect(() => {
    if (paymentMethod || isAmend) {
      handleLoadCashInfo();
    }
  }, [handleLoadCashInfo, paymentMethod, isAmend]);

  useEffect(() => {
    if (
      isPaymentMethodDisabled ||
      (isPaymentDayTradeDisabled &&
        defaultPaymentMethod === PaymentMethod.DayTrade)
    ) {
      setPaymentMethod(PaymentMethod.TradingBalance);
    }

    if (isAmend && isDayTrade) {
      setPaymentMethod(PaymentMethod.DayTrade);
    }
  }, [isPaymentMethodDisabled, isPaymentDayTradeDisabled, isAmend, isDayTrade]);

  useEffect(() => {
    form.validateFields();

    clevertapBuyOrderViewed(symbol, modalData?.source);

    // Reset payment method if symbol changed from search
    if (modalData?.openFromSearch) {
      handleSetPaymentMethod(PaymentMethod.TradingBalance);

      setModalState({
        modalData: {
          ...modalData,
          openFromSearch: false,
        },
      });
    }
  }, []);

  return (
    <>
      <DayTradeLeverage
        zIndex={1001}
        balance={leverageBalanceList}
        tradingBalance={tradingbalance}
        error={{
          [PaymentMethod.TradingLimit]: !!cashInfoError,
          [PaymentMethod.DayTrade]: !!cashInfoError,
        }}
        methodValue={paymentMethod}
        visible={isPaymentMethodVisible}
        loading={isCashInfoLoading}
        onMethodChange={handleMethodChange}
        onCancel={handleMethodClose}
        onReloadClick={() => loadCashInfo(symbol)}
        destroyOnClose
        symbol={symbol}
        splitOrderActive={isSplitOrderActive}
        dayTradeAvailable={isDayTradeSubAccountActive}
        dayTradeMultiplier={dayTradeMultiplier}
        onShowDayTradeOnboarding={onShowDayTradeOnboarding}
      />

      <If condition={marketHour.suspend_info}>
        <SpecialMonitoringBanner
          onClick={showSpecialMonitoringModal}
          title={marketHour.suspend_info}
        />
      </If>

      <TransactionForm
        form={form}
        colon={false}
        labelCol={{ span: 12 }}
        onFinish={handleSubmit}
        onFieldsChange={handleFieldsChange}
        onBlur={handleSaveFields}
        onValuesChange={handleSaveFields}
      >
        <FieldsWrapper>
          <If condition={isShowOrderType}>
            <OrderTypeFormItem
              form={form}
              transactionType={TransactionType.Buy}
              defaultValue={buyOrderType as BuyOrderType}
              onChange={handleOrderTypeChange}
              onInfoIconClick={handleOrderTypeInfoClick}
            />
          </If>

          <If condition={!isPaymentMethodDisabled || isShowAmendLeverage}>
            <Form.Item label="Leverage">
              <BuyLeverage
                paymentMethod={paymentMethod}
                multiplier={dayTradeMultiplier}
                enableDayTrade={dayTradeMultiplier > 1}
                isAmend={isAmend}
                onMethodChange={handleMethodChange}
                onOpenModalLeverage={handleBalanceLabelClick}
              />
            </Form.Item>
          </If>

          <Form.Item
            label={
              <BalanceButton
                type="button"
                onClick={handleBalanceLabelClick}
                disabled
              >
                {getBalanceLabel()}

                <If condition={!isPaymentMethodDisabled}>
                  <Tooltip
                    placement="bottom"
                    title="Tingkatkan buying power kamu dengan Trading Limit"
                    visible={isTradingLimitTooltipActive}
                    onVisibleChange={handleTradingLimitTooltipChange}
                    trigger="click"
                    arrowPointAtCenter
                  >
                    <If condition={isTradingLimitTooltipActive}>
                      <Icon
                        component={ChevronDownSvg}
                        style={{ color: '#969696' }}
                      />
                    </If>
                  </Tooltip>
                </If>
              </BalanceButton>
            }
          >
            <Text family="bold">
              {formatNumber(
                tradingbalance,
                true,
                isOverlimit ? '0,0.00' : '0,0',
              )}
            </Text>
          </Form.Item>

          <If condition={!isAmend || (isAmend && (isAutoBuy || isSaham))}>
            <Form.Item
              className="slider-form-item"
              label=""
              wrapperCol={{
                xs: { offset: 0 },
                sm: { span: 12, offset: 12 },
              }}
            >
              <Flex align="center">
                <Slider
                  min={0}
                  max={getSliderMaxLot()}
                  value={lot}
                  tracksWidth={tracksWidth}
                  tipFormatter={null}
                  onChange={(newLot) => {
                    // If newLot is more than max Lot, don't update the value
                    if (isAmend && newLot > getMaxLot()) {
                      // if ordered price is same as price, set shares to ordered lot (max lot)
                      if (orderedPrice === price) {
                        setShares(orderedLot * lotSize);
                      }

                      return;
                    }

                    clevertapOrderQuantityAdjusted({
                      symbol,
                      orderType,
                      adjustedType: 'slider',
                    });
                    setShares(Math.ceil(newLot) * lotSize);
                    handleValidateAllFields();
                  }}
                />
                <SliderPercentText>{getPercentage()}%</SliderPercentText>
              </Flex>
            </Form.Item>
          </If>

          <Form.Item label="Investment (Plus Fee)">
            <Text family="bold">
              Rp{' '}
              {formatNumber(
                investmentPlusFee,
                true,
                isOverlimit ? '0,0.00' : '0,0',
              )}
            </Text>
          </Form.Item>

          <If condition={isAutoBuy}>
            <Form.Item
              label={<TriggerPriceTooltip />}
              data-cy={DATA_CY_MODAL_TRADING.FORM_TRIGGER_PRICE}
            >
              <Flex align="center">
                <TriggerPriceSelect
                  value={triggerPriceOption[configType - 1]}
                  onChange={(e) => {
                    setConfigType(e.target.value);

                    trackAutoBuyTriggerConditionSelected(
                      { 1: '≥', 2: '"≤"' }[e.target.value],
                    );
                    handleValidateAllFields();
                  }}
                  data-cy={DATA_CY_MODAL_TRADING.TRIGGER_PRICE_SELECT}
                >
                  {triggerPriceOption.map((opt) => (
                    <option key={opt}>{opt}</option>
                  ))}
                </TriggerPriceSelect>
                <InputStepper
                  containerStyle={{ marginLeft: 4, height: 32 }}
                  defaultValue={triggerPrice}
                  value={triggerPrice}
                  min={0}
                  step={1}
                  onChange={(trigger) => {
                    setTriggerPrice(trigger);
                    handleValidateAllFields();
                  }}
                />
              </Flex>
            </Form.Item>
          </If>

          <Form.Item
            label={isAutoBuy ? 'Buy Price' : 'Price'}
            data-cy={
              isAutoBuy
                ? DATA_CY_MODAL_TRADING.FORM_BUY_PRICE
                : DATA_CY_MODAL_TRADING.FORM_PRICE
            }
          >
            <InputStepper
              value={price}
              min={0}
              step={getStepFractionByValue(price)}
              onChange={handlePriceChange}
              disableDecimal
            />
          </Form.Item>

          <Form.Item
            label="Buy Order Lot"
            data-cy={DATA_CY_MODAL_TRADING.FORM_LOT}
          >
            <InputStepper
              defaultValue={0}
              min={0}
              value={lot}
              max={getMaxLot()}
              onChange={handleLotChange}
            />
          </Form.Item>

          <If condition={isOverlimit}>
            <OverLimitText isRight={isRight} />
          </If>

          <If condition={!hideExpiry}>
            <Form.Item label="Expiry">
              <CustomSelect
                value={expiry}
                onChange={handleExpiryChange}
                style={{ width: '160px' }}
                disabled={isPaymentDayTradeActive}
                daya-cy={DATA_CY_MODAL_TRADING.EXPIRY_SELECT}
              >
                {Object.entries(expiryOptions).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </CustomSelect>
            </Form.Item>
          </If>

          <If condition={showSplitOrderForm}>
            <SplitOrderFormItem
              form={form}
              orderLot={lot}
              checkedInitialValue={isSplitOrderActive}
              quantityInitialValue={splitQuantity}
              minRangeInitialValue={splitRangeMin}
              maxRangeInitialValue={splitRangeMax}
              maxQuantity={maxOrder}
              transactionType={TransactionType.Buy}
              onInfoIconClick={onShowSplitOrderInfo}
              onSwitchChange={handleChangeSwitchSplitOrder}
              onSplitMethodInfoClick={onShowSplitOrderMethodInfo}
            />
          </If>
        </FieldsWrapper>

        <ButtonWrapper>
          <OrderButton
            disabled={disableBuy}
            type="primary"
            htmlType="submit"
            radius="4px"
            block
            height="40px"
            data-cy={DATA_CY_MODAL_TRADING.BUTTON_PLACE_ORDER}
          >
            <Text>Place Order</Text>
          </OrderButton>
        </ButtonWrapper>
      </TransactionForm>
    </>
  );
};

export default memo(FormBuy);
